package com.tovtalk.utils.deeplink

import com.tovtalk.utils.logger.Logger

/**
 * Deep link route – single source of truth for URL structure.
 * Base: https://&lt;app-domain&gt;/ or tovtalk://
 * - Search: .../search or .../search?q=...
 * - User: .../user/{username} or .../profile/{username}
 * - Post/Video: .../post/{postRef} or .../video/{postRef}
 * - Chat: .../chat/{userId}
 */

/** Base URL for shareable deep links (web app). */
private const val DEEP_LINK_BASE = "https://tovtalk.com"

object DeepLinkBuilder {
    fun buildProfileUrl(username: String): String =
        "$DEEP_LINK_BASE/u/${username}"

    fun buildPostUrl(postRef: String): String =
        "$DEEP_LINK_BASE/p/$postRef"

    fun buildChatUrl(userId: Int): String =
        "$DEEP_LINK_BASE/chat/$userId"
}
sealed class DeepLinkRoute {
    data object Home : DeepLinkRoute()
    data class Search(val query: String? = null) : DeepLinkRoute()
    data class User(val username: String) : DeepLinkRoute()
    data class Post(val postRef: String) : DeepLinkRoute()
    data class Video(val postRef: String) : DeepLinkRoute()
    /** Open chat with this user (e.g. from notification – senderId is the other user). */
    data class Chat(val userId: Int) : DeepLinkRoute()
    /** Info pages: /privacy or /privacy-policy → show privacy screen. */
    data object Privacy : DeepLinkRoute()
    /** /terms – תנאי שימוש, תוכן מ־API. */
    data object Terms : DeepLinkRoute()
    /** /about – אודות, תוכן מ־API. */
    data object About : DeepLinkRoute()
    /** הוראות מחיקת חשבון – /account-deletion, תוכן מ־API. */
    data object AccountDeletion : DeepLinkRoute()
    /** Dynamic admin-managed content page – /{slug}, validated against /v2/pages/doc-slugs at Splash. */
    data class StaticPage(val slug: String) : DeepLinkRoute()
    data object Unknown : DeepLinkRoute()
}

/**
 * Parses a URL string into a [DeepLinkRoute].
 * Handles both https and tovtalk:// schemes.
 */
object DeepLinkParser {
    private const val SCHEME_HTTPS = "https"
    private const val SCHEME_HTTP = "http"
    private const val SCHEME_APP = "tovtalk"
    private const val PATH_SEARCH = "search"
    private const val PATH_USER = "user"
    private const val PATH_U = "u"
    private const val PATH_PROFILE = "profile"
    private const val PATH_POST = "post"
    private const val PATH_P = "p"
    private const val PATH_VIDEO = "video"
    private const val PATH_COMMENTS = "comments"
    private const val PATH_CHAT = "chat"
    private const val PATH_PRIVACY = "privacy"
    private const val PATH_PRIVACY_POLICY = "privacy-policy"
    private const val PATH_TERMS = "terms"
    private const val PATH_ABOUT = "about"
    private const val PATH_ACCOUNT_DELETION = "account-deletion"
    private const val QUERY_Q = "q"

    private val SLUG_REGEX = Regex("^[a-z0-9][a-z0-9\\-_]{0,63}$")
    private fun isValidSlug(segment: String): Boolean =
        SLUG_REGEX.matches(segment.lowercase())

    fun parse(url: String): DeepLinkRoute {
        val trimmed = url.trim()
        if (trimmed.isBlank()) {
            Logger.i("[DeepLink] נכנס URL ריק → יעד: Home", "DeepLink")
            return DeepLinkRoute.Home
        }
        Logger.i("[DeepLink] נכנס URL: $trimmed", "DeepLink")
        val (scheme, host, pathSegments, queryParams) = parseUrl(trimmed) ?: run {
            Logger.w("[DeepLink] פרסור נכשל → יעד: Unknown", "DeepLink")
            return DeepLinkRoute.Unknown
        }
        val schemeLower = scheme.lowercase()
        if (schemeLower != SCHEME_HTTPS && schemeLower != SCHEME_HTTP && schemeLower != SCHEME_APP) {
            Logger.w("[DeepLink] סכמה לא נתמכת: $scheme → יעד: Unknown", "DeepLink")
            return DeepLinkRoute.Unknown
        }
        val route: DeepLinkRoute
        if (pathSegments.isEmpty()) {
            route = DeepLinkRoute.Home
        } else if (pathSegments[0].equals(PATH_SEARCH, ignoreCase = true)) {
            val q = queryParams[QUERY_Q]?.firstOrNull()
            route = DeepLinkRoute.Search(query = q)
        } else if ((pathSegments[0].equals(PATH_USER, ignoreCase = true) ||
                pathSegments[0].equals(PATH_U, ignoreCase = true)) && pathSegments.size >= 2) {
            route = DeepLinkRoute.User(username = pathSegments[1])
        } else if (pathSegments[0].equals(PATH_PROFILE, ignoreCase = true) && pathSegments.size >= 2) {
            route = DeepLinkRoute.User(username = pathSegments[1])
        } else if ((pathSegments[0].equals(PATH_POST, ignoreCase = true) ||
                pathSegments[0].equals(PATH_P, ignoreCase = true) ||
                pathSegments[0].equals(PATH_VIDEO, ignoreCase = true) ||
                pathSegments[0].equals(PATH_COMMENTS, ignoreCase = true)) && pathSegments.size >= 2) {
            route = DeepLinkRoute.Post(postRef = pathSegments[1])
        } else if (pathSegments[0].equals(PATH_CHAT, ignoreCase = true) && pathSegments.size >= 2) {
            route = pathSegments[1].toIntOrNull()?.takeIf { it > 0 }?.let { DeepLinkRoute.Chat(userId = it) } ?: DeepLinkRoute.Unknown
        } else if (pathSegments[0].equals(PATH_PRIVACY, ignoreCase = true) ||
            pathSegments[0].equals(PATH_PRIVACY_POLICY, ignoreCase = true)) {
            route = DeepLinkRoute.Privacy
        } else if (pathSegments[0].equals(PATH_TERMS, ignoreCase = true)) {
            route = DeepLinkRoute.Terms
        } else if (pathSegments[0].equals(PATH_ABOUT, ignoreCase = true)) {
            route = DeepLinkRoute.About
        } else if (pathSegments[0].equals(PATH_ACCOUNT_DELETION, ignoreCase = true)) {
            route = DeepLinkRoute.AccountDeletion
        } else if (pathSegments.size == 1 && isValidSlug(pathSegments[0])) {
            route = DeepLinkRoute.StaticPage(slug = pathSegments[0].lowercase())
        } else {
            route = DeepLinkRoute.Unknown
        }
        val target: String = if (route is DeepLinkRoute.Home) "Home"
        else if (route is DeepLinkRoute.Search) "Search (q=${route.query})"
        else if (route is DeepLinkRoute.User) "Profile @${route.username}"
        else if (route is DeepLinkRoute.Post) "Post ref=${route.postRef}"
        else if (route is DeepLinkRoute.Video) "Video ref=${route.postRef}"
        else if (route is DeepLinkRoute.Chat) "Chat userId=${route.userId}"
        else if (route is DeepLinkRoute.Privacy) "Privacy"
        else if (route is DeepLinkRoute.Terms) "Terms"
        else if (route is DeepLinkRoute.About) "About"
        else if (route is DeepLinkRoute.AccountDeletion) "AccountDeletion"
        else if (route is DeepLinkRoute.StaticPage) "StaticPage(${route.slug})"
        else "Unknown"
        Logger.i("[DeepLink] פרסור: $route → יעד: $target", "DeepLink")
        return route
    }

    private fun parseUrl(url: String): UrlParts? {
        val schemeEnd = url.indexOf("://")
        if (schemeEnd <= 0) return null
        val scheme = url.substring(0, schemeEnd)
        var rest = url.substring(schemeEnd + 3)
        val pathStart = rest.indexOf('/')
        val host = if (pathStart < 0) rest else rest.substring(0, pathStart)
        val pathAndQuery = if (pathStart < 0) "" else rest.substring(pathStart)
        val queryStart = pathAndQuery.indexOf('?')
        val path = if (queryStart < 0) pathAndQuery else pathAndQuery.substring(0, queryStart)
        val query = if (queryStart < 0) "" else pathAndQuery.substring(queryStart + 1)
        val pathSegments = path.split('/').filter { it.isNotBlank() }
        val queryParams = query.split('&')
            .mapNotNull { part ->
                val eq = part.indexOf('=')
                if (eq < 0) null else part.substring(0, eq) to part.substring(eq + 1)
            }
            .groupBy({ it.first }, { it.second })
        return UrlParts(scheme, host, pathSegments, queryParams)
    }

    private data class UrlParts(
        val scheme: String,
        val host: String,
        val pathSegments: List<String>,
        val queryParams: Map<String, List<String>>
    )
}
